import React from "react";
import { Image, Group, Stack, Box, AspectRatio } from "@mantine/core";
import { Card } from "./UI/Card";
import { Badge } from "./UI/Badge";
import { Heading3, Heading4, DimmedText, MainText } from "./UI/Typography";
import {
  IconCode,
  IconPalette,
  IconBulb,
  IconUsers,
  IconTrendingUp,
} from "@tabler/icons-react";

interface Project {
  id: number;
  slug: string;
  title: string;
  description: string;
  client_name?: string;
  project_url?: string;
  project_type: string;
  featured_image_url: string;
  skills: string[];
  tools: string[];
  duration?: string;
}

interface ProjectCardProps {
  project: Project;
  variant?: "default" | "compact";
  onClick?: () => void;
}

const getProjectIcon = (projectType: string) => {
  switch (projectType) {
    case "Software Development":
      return <IconCode size={20} />;
    case "Digital Media":
      return <IconPalette size={20} />;
    case "Teaching & Events":
      return <IconUsers size={20} />;
    case "Marketing & Strategy":
      return <IconTrendingUp size={20} />;
    default:
      return <IconBulb size={20} />;
  }
};

const getProjectGradient = (projectType: string) => {
  switch (projectType) {
    case "Technical Development":
      return "linear-gradient(135deg, rgba(212,85,43,0.85) 0%, rgba(212,85,43,0.85) 100%)";
    case "Digital Media":
      return "linear-gradient(135deg, rgba(212,85,43,0.85) 0%, rgba(184,132,61,0.85) 100%)";
    case "Marketing & Strategy":
      return "linear-gradient(135deg, rgba(212,85,43,0.85) 0%, rgba(212,85,43,0.85) 100%)";
    case "Teaching & Events":
      return "linear-gradient(135deg, rgba(0,48,56,0.85) 0%, rgba(212,85,43,0.85) 100%)";
    case "Business Consulting":
      return "linear-gradient(135deg, rgba(212,85,43,0.85) 0%, rgba(166,62,0,0.85) 100%)";
    default:
      return "linear-gradient(135deg, rgba(212,85,43,0.85) 0%, rgba(212,85,43,0.85) 100%)";
  }
};

const glassHover = {
  transform: "translateY(-8px)",
  boxShadow:
    "0 24px 64px rgba(212,85,43,0.22), 0 0 0 1px rgba(212,85,43,0.28), inset 0 1px 0 rgba(255,255,255,0.1)",
  border: "1px solid rgba(212,85,43,0.28)",
};

const glassRest = {
  transform: "translateY(0)",
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.07), 0 8px 32px rgba(0,0,0,0.4)",
  border: "1px solid rgba(212,85,43,0.12)",
};

export const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  variant = "default",
  onClick,
}) => {
  const [hovered, setHovered] = React.useState(false);
  const isCompact = variant === "compact";

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      window.location.href = `/tech/${project.slug}`;
    }
  };

  if (isCompact) {
    return (
      <Card
        shadow="md"
        padding="md"
        radius="md"
        onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          cursor: "pointer",
          ...(hovered ? glassHover : glassRest),
        }}
      >
        <Card.Section>
          <Box style={{ position: "relative", overflow: "hidden" }}>
            <Image
              src={
                project.featured_image_url ||
                "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=200&fit=crop"
              }
              height={120}
              alt={project.title}
              fit="cover"
              style={{
                transition: "transform 0.35s ease",
                transform: hovered ? "scale(1.05)" : "scale(1)",
                width: "100%",
              }}
            />
            <Box
              style={{
                position: "absolute",
                top: 0, left: 0, right: 0, bottom: 0,
                background: getProjectGradient(project.project_type),
                opacity: hovered ? 0.2 : 0.5,
                transition: "opacity 0.35s ease",
              }}
            />
          </Box>
        </Card.Section>

        <Stack gap="sm" mt="md">
          <Heading4
            style={{
              lineHeight: 1.3,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {project.title}
          </Heading4>
          <Badge
            size="xs"
            style={{
              background: getProjectGradient(project.project_type).replace("0.85", "0.2"),
              color: "white",
              border: "none",
            }}
          >
            {project.project_type}
          </Badge>
        </Stack>
      </Card>
    );
  }

  return (
    <Card
      shadow="xl"
      padding={0}
      radius="md"
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        overflow: "hidden",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "pointer",
        ...(hovered ? glassHover : glassRest),
      }}
    >
      <AspectRatio ratio={16 / 9} style={{ position: "relative", overflow: "hidden" }}>
        <Image
          src={
            project.featured_image_url ||
            "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=200&fit=crop"
          }
          alt={project.title}
          fit="cover"
          style={{
            transition: "transform 0.4s ease",
            transform: hovered ? "scale(1.08)" : "scale(1)",
            width: "100%",
            height: "100%",
          }}
        />
        <Box
          style={{
            position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
            background: getProjectGradient(project.project_type),
            opacity: hovered ? 0.2 : 0.55,
            transition: "opacity 0.4s ease",
          }}
        />
        <Badge
          size="lg"
          radius="md"
          style={{
            textTransform: "capitalize",
            position: "absolute",
            top: 16,
            right: 16,
            background: "rgba(18,18,18,0.7)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: "1px solid rgba(212,85,43,0.2)",
            color: "white",
            zIndex: 1,
          }}
          leftSection={getProjectIcon(project.project_type)}
        >
          {project.project_type}
        </Badge>
      </AspectRatio>

      <Stack gap="md" p="xl">
        <Heading3 style={{ lineHeight: 1.3 }}>{project.title}</Heading3>

        {project.description && (
          <DimmedText
            size="sm"
            style={{
              lineHeight: 1.6,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {project.description}
          </DimmedText>
        )}

        {(project.client_name || project.duration) && (
          <Group gap="xl">
            {project.client_name && (
              <MainText size="xs" style={{ color: "#7ba4a5", fontWeight: 500 }}>
                Client: {project.client_name}
              </MainText>
            )}
            {project.duration && (
              <DimmedText size="xs">{project.duration}</DimmedText>
            )}
          </Group>
        )}

        <Group gap="xs">
          {project.skills.slice(0, 3).map((skill, index) => (
            <Badge
              key={index}
              size="sm"
              radius="md"
              variant="skills"
              style={{ textTransform: "capitalize", fontWeight: 500 }}
            >
              {skill}
            </Badge>
          ))}
          {project.skills.length > 3 && (
            <Badge size="sm" radius="md" variant="light">
              +{project.skills.length - 3}
            </Badge>
          )}
        </Group>
      </Stack>
    </Card>
  );
};
